import getEnv from '@/config/env';
import { BaseError } from '@/config/http';
import {
  AxiosRequestHeaders,
  AxiosStatic,
  Method,
  AxiosRequestTransformer,
  AxiosResponseTransformer,
  AxiosAdapter,
  AxiosBasicCredentials,
  AxiosProxyConfig,
  CancelToken,
  TransitionalOptions,
  AxiosRequestConfig,
} from 'axios';

const CASE_BASE_URL = 'cm';

export const CANCEL_PREV_REQUEST_DEFAULT: boolean = true;

export const MICRO_SERVER_URL_CASE = `${getEnv('MICRO_SERVER_URL_CASE')}/${CASE_BASE_URL}`;

export const MICRO_SERVER_URL_SCM = `${getEnv('MICRO_SERVER_URL_SCM')}/${CASE_BASE_URL}`;

export const MICRO_SERVER_URL_SLA = `${getEnv('MICRO_SERVER_URL_SLA')}/${CASE_BASE_URL}`;

export const MICRO_SERVER_URL_TAGS = `${getEnv('MICRO_SERVER_URL_TAGS')}/${CASE_BASE_URL}`;

export enum HttpMethod {
  GET = 'get',
  DELETE = 'delete',
  HEAD = 'head',
  OPTIONS = 'options',
  POST = 'post',
  PUT = 'put',
  PATCH = 'patch',
}

export type InferFirst<T extends unknown[]> = T extends [p: infer P, ...r: unknown[]] ? P : never;
export type InferLast<T extends unknown[]> = T extends [...r: unknown[], p: infer P] ? P : never;

export type StaticUrl = string;
export type DynamicUrl = (urlArg: string | number) => string;

export interface ApiConfig<T extends StaticUrl | DynamicUrl = StaticUrl> {
  url: T;
  method?: HttpMethod;
  headers?: AxiosRequestHeaders;
  cancelPrev?: boolean;
  embeddedField?: string;
  ignoreError?: boolean;
  withCredentials?: boolean;
  axiosInstance?: AxiosStatic;
}

export interface SseConfig<T extends StaticUrl | DynamicUrl = StaticUrl> {
  url: T;
  withCredentials?: boolean | undefined;
  headers?: object | undefined;
  proxy?: string | undefined;
  https?: object | undefined;
  rejectUnauthorized?: boolean | undefined;
}

export interface State<T> {
  loading: boolean;
  error: BaseError | null;
  data: T | null;
}

export interface PageState<T extends any[]> extends State<T> {
  total: number;
  page: number;
  per_page: number;
  data: T;
}

export interface Run<T> {
  (): Promise<T | null>;
  (runtimeConfig?: RuntimeConfig<any>): Promise<T | null>;
}

export interface RunWithPayload<T, P> {
  (payload: P): Promise<T | null>;
  (payload: P, runtimeConfig?: RuntimeConfig<P>): Promise<T | null>;
}

export interface RunWithUrlArg<T> {
  (urlArg: string | number): Promise<T | null>;
  (urlArg: string | number, runtimeConfig?: RuntimeConfig<any>): Promise<T | null>;
}
export interface RunWithUrlArgAndPayload<T, P> {
  (urlArg: string | number, payload: P): Promise<T | null>;
  (urlArg: string | number, payload: P, runtimeConfig?: RuntimeConfig<P>): Promise<T | null>;
}

export type RunWith<T, P> = Run<T> | RunWithPayload<T, P> | RunWithUrlArg<T> | RunWithUrlArgAndPayload<T, P>;

export class RuntimeConfig<P> {
  constructor(config?: AxiosRequestConfig<P>) {
    if (!config) return;
    Object.keys(config).forEach((k) => {
      this[k as keyof RuntimeConfig<P>] = config[k as keyof AxiosRequestConfig<P>];
    });
  }
  static create<P>(config?: AxiosRequestConfig<P>) {
    return new RuntimeConfig<P>(config);
  }
  url?: string;
  method?: Method;
  baseURL?: string;
  transformRequest?: AxiosRequestTransformer | AxiosRequestTransformer[];
  transformResponse?: AxiosResponseTransformer | AxiosResponseTransformer[];
  headers?: AxiosRequestHeaders;
  params?: any;
  paramsSerializer?: (params: any) => string;
  data?: P;
  timeout?: number;
  timeoutErrorMessage?: string;
  withCredentials?: boolean;
  adapter?: AxiosAdapter;
  auth?: AxiosBasicCredentials;
  responseType?: ResponseType;
  xsrfCookieName?: string;
  xsrfHeaderName?: string;
  onUploadProgress?: (progressEvent: any) => void;
  onDownloadProgress?: (progressEvent: any) => void;
  maxContentLength?: number;
  validateStatus?: ((status: number) => boolean) | null;
  maxBodyLength?: number;
  maxRedirects?: number;
  socketPath?: string | null;
  httpAgent?: any;
  httpsAgent?: any;
  proxy?: AxiosProxyConfig | false;
  cancelToken?: CancelToken;
  decompress?: boolean;
  transitional?: TransitionalOptions;
  signal?: AbortSignal;
  insecureHTTPParser?: boolean;
  ignoreError?: boolean;
  private __proto__: object = RuntimeConfig.prototype;
}
